/* eslint-disable no-nested-ternary */
/* eslint-disable no-unused-vars */
import { useState, useCallback, useEffect } from 'react';
import useAuth from '../../../hooks/useAuth';
import professions from './professions';
import {
  Autocomplete,
  IconButton,
  Switch,
  TextField,
  Tooltip,
  Typography,
  Box,
  Grid,
  Divider,
  Chip,
  Alert
} from '@material-ui/core';
import * as moment from 'moment';
import PropTypes from 'prop-types';
import useMounted from '../../../hooks/useMounted';
import { customerApi } from '../../../api/customerApi';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import toast from 'react-hot-toast';
import BusinessIcon from '@mui/icons-material/Business';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import LockIcon from '@mui/icons-material/Lock';
import LinkIcon from '@mui/icons-material/Link';
import EventIcon from '@mui/icons-material/Event';
import SecurityIcon from '@mui/icons-material/Security';
import CampaignIcon from '@mui/icons-material/Campaign';
import ApartmentIcon from '@mui/icons-material/Apartment';
import { alpha } from '@mui/material/styles';
import { getTone } from '../../../theme/tones';

const GlassSubCard = ({ children, title, icon: Icon, color = "primary.main" }) => (
  <Box sx={{ 
    p: 3, 
    borderRadius: '24px', 
    bgcolor: (theme) => alpha(theme.palette.common.white, 0.4), 
    border: (theme) => `1px solid ${alpha(theme.palette.common.white, 0.6)}`,
    height: '100%',
    transition: 'all 0.3s ease',
    '&:hover': {
      bgcolor: (theme) => alpha(theme.palette.common.white, 0.6),
      boxShadow: (theme) => `0 8px 24px ${alpha(theme.palette.common.black, 0.04)}`
    }
  }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
      <Box sx={{ p: 1, borderRadius: '12px', bgcolor: color, color: 'white', display: 'flex' }}>
        <Icon fontSize="small" />
      </Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'primary.main', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.5px' }}>
        {title}
      </Typography>
    </Box>
    {children}
  </Box>
);

const InfoLine = ({ label, value, editing, children }) => (
  <Box sx={{ mb: 2 }}>
    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, mb: 0.5, display: 'block' }}>
      {label}
    </Typography>
    <Box>
      {editing ? children : (
        <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.main' }}>
          {value || '—'}
        </Typography>
      )}
    </Box>
  </Box>
);

const contactMethodOptions = [
  { label: "E-mail", value: "email" },
  { label: "Téléphone", value: "phone" },
  { label: "SMS", value: "sms" },
  { label: "WhatsApp", value: "whatsapp" },
  { label: "Messenger", value: "messenger" },
  { label: "Visioconférence", value: "video_call" },
  { label: "Courrier postal", value: "postal" }
];

const timeOptions = [
  { label: "Matin (8h-12h)", value: "morning" },
  { label: "Après-midi (12h-18h)", value: "afternoon" },
  { label: "Soirée (18h-21h)", value: "evening" },
  { label: "Week-end uniquement", value: "weekend" },
  { label: "Jours ouvrés uniquement", value: "weekdays" },
  { label: "Indifférent", value: "any" }
];

const CustomerProInfos = (props) => {
  const { profession, specialite, modeOfExercise, adeliRpps, numSecu, mdpMondpc, editing, mdpFifpl, remplacant, notCall, notRelaunch, contactMethod, contactTime, linkedinProfileLink, siret, etablissement, startRelaunchDate,
    errors, handleBlur, handleChange, touched, values, setFieldValue } = props;
  
  const modeOfExerciseLabel = ['Libéral', 'Salarié en centre de santé', 'Salarié en hôpital', 'Autre'];
  const mounted = useMounted();
  const [displayPasswords, setDisplayPasswords] = useState(false);
  const [specialiteTab, setSpecialiteTab] = useState(['']);

  const getDisplayPasswordsCustomer = useCallback(async () => {
    try {
      const data = await customerApi.getDisplayPasswordsCustomer(localStorage.getItem('accessToken'));
      if (mounted.current) setDisplayPasswords(data);
    } catch (err) { console.error(err); }
  }, [mounted]);

  useEffect(() => {
    getDisplayPasswordsCustomer();
  }, [getDisplayPasswordsCustomer]);

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: (theme) => alpha(theme.palette.common.black, 0.01) }}>
      <Grid container spacing={3}>
        <Grid item xs={12} lg={7}>
          <GlassSubCard title="Carrière & Spécialité" icon={BusinessIcon} color={getTone('blue').deep}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <InfoLine label="Métier / Profession" value={profession} editing={editing}>
                  <Autocomplete
                    getOptionLabel={(o) => o.profession}
                    options={professions}
                    size="small"
                    onChange={(e, v) => setFieldValue('profession', v?.profession || profession)}
                    renderInput={(p) => <TextField {...p} variant="outlined" size="small" />}
                  />
                </InfoLine>
              </Grid>
              <Grid item xs={12} md={6}>
                <InfoLine label="Spécialité" value={specialite} editing={editing}>
                  <Autocomplete
                    options={specialiteTab}
                    size="small"
                    onChange={(e, v) => setFieldValue('specialite', v || specialite)}
                    renderInput={(p) => <TextField {...p} variant="outlined" size="small" />}
                  />
                </InfoLine>
              </Grid>
              <Grid item xs={12} md={6}>
                <InfoLine label="Type d'exercice" value={modeOfExercise} editing={editing}>
                  <Autocomplete
                    options={modeOfExerciseLabel}
                    size="small"
                    onChange={(e, v) => setFieldValue('modeOfExercise', v || modeOfExercise)}
                    renderInput={(p) => <TextField {...p} variant="outlined" size="small" />}
                  />
                </InfoLine>
              </Grid>
              <Grid item xs={12} md={6}>
                <InfoLine label="Établissement" value={etablissement} editing={editing}>
                  <TextField size="small" name="etablissement" value={values.etablissement} onChange={handleChange} fullWidth variant="outlined" />
                </InfoLine>
              </Grid>
              <Grid item xs={12} md={6}>
                <InfoLine label="RPPS / ADELI" value={adeliRpps} editing={editing}>
                  <TextField size="small" name="adeliRpps" value={values.adeliRpps} onChange={handleChange} fullWidth variant="outlined" />
                </InfoLine>
              </Grid>
              <Grid item xs={12} md={6}>
                <InfoLine label="SIRET" value={siret} editing={editing}>
                  <TextField size="small" name="siret" value={values.siret} onChange={handleChange} fullWidth variant="outlined" />
                </InfoLine>
              </Grid>
            </Grid>
          </GlassSubCard>
        </Grid>

        <Grid item xs={12} lg={5}>
          <GlassSubCard title="Sécurité & Accès" icon={SecurityIcon} color={getTone('rose').base}>
            {displayPasswords ? (
              <Box sx={{ mb: 3 }}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <InfoLine label="MondPC PWD" value={mdpMondpc} editing={editing}>
                      <TextField type="password" size="small" name="mdpMondpc" value={values.mdpMondpc} onChange={handleChange} fullWidth variant="outlined" />
                    </InfoLine>
                  </Grid>
                  <Grid item xs={6}>
                    <InfoLine label="FIFPL PWD" value={mdpFifpl} editing={editing}>
                      <TextField type="password" size="small" name="mdpFifpl" value={values.mdpFifpl} onChange={handleChange} fullWidth variant="outlined" />
                    </InfoLine>
                  </Grid>
                </Grid>
              </Box>
            ) : (
              <Alert severity="info" sx={{ mb: 3, borderRadius: '12px' }}>MDP masqués</Alert>
            )}
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, borderRadius: '16px', bgcolor: (theme) => alpha(theme.palette.common.black, 0.03) }}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>Remplaçant</Typography>
                {editing ? <Switch checked={values.remplacant} onChange={handleChange} name="remplacant" /> : <Chip label={remplacant ? 'OUI' : 'NON'} size="small" color={remplacant ? 'primary' : 'default'} />}
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, borderRadius: '16px', bgcolor: (theme) => alpha(theme.palette.common.black, 0.03) }}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>Ne pas appeler</Typography>
                {editing ? <Switch checked={values.notCall === 'Oui'} onChange={(e) => setFieldValue('notCall', e.target.checked ? 'Oui' : 'Non')} /> : <Chip label={notCall === 'Oui' ? 'OUI' : 'NON'} size="small" color={notCall === 'Oui' ? 'error' : 'default'} />}
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, borderRadius: '16px', bgcolor: (theme) => alpha(theme.palette.common.black, 0.03) }}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>Relances auto</Typography>
                {editing ? <Switch checked={!values.notRelaunch} onChange={(e) => setFieldValue('notRelaunch', !e.target.checked)} /> : <Chip label={values.notRelaunch ? 'OFF' : 'ON'} size="small" variant="outlined" />}
              </Box>
            </Box>
          </GlassSubCard>
        </Grid>

        <Grid item xs={12}>
          <GlassSubCard title="Marketing & Relances" icon={CampaignIcon} color={getTone('sky').color}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={3}>
                <InfoLine label="Profil LinkedIn" value={linkedinProfileLink} editing={editing}>
                  <TextField size="small" name="linkedinProfileLink" value={values.linkedinProfileLink} onChange={handleChange} fullWidth variant="outlined" />
                </InfoLine>
              </Grid>
              <Grid item xs={12} md={3}>
                <InfoLine label="Date début de relance" value={startRelaunchDate ? moment(startRelaunchDate).format('DD/MM/YYYY') : 'Pas de date'} editing={editing}>
                  <TextField type="date" size="small" name="startRelaunchDate" value={values.startRelaunchDate} onChange={handleChange} fullWidth variant="outlined" InputLabelProps={{ shrink: true }} />
                </InfoLine>
              </Grid>
              <Grid item xs={12} md={3}>
                <InfoLine label="Canal préféré" value={contactMethodOptions.find(o => o.value === contactMethod)?.label} editing={editing}>
                  <Autocomplete
                    options={contactMethodOptions}
                    getOptionLabel={(o) => o.label}
                    size="small"
                    value={contactMethodOptions.find(o => o.value === (values.contactMethod || contactMethod))}
                    onChange={(e, v) => setFieldValue('contactMethod', v?.value || 'email')}
                    renderInput={(p) => <TextField {...p} variant="outlined" />}
                  />
                </InfoLine>
              </Grid>
              <Grid item xs={12} md={3}>
                <InfoLine label="Moment préféré" value={timeOptions.find(o => o.value === contactTime)?.label} editing={editing}>
                  <Autocomplete
                    options={timeOptions}
                    getOptionLabel={(o) => o.label}
                    size="small"
                    value={timeOptions.find(o => o.value === (values.contactTime || contactTime))}
                    onChange={(e, v) => setFieldValue('contactTime', v?.value || 'any')}
                    renderInput={(p) => <TextField {...p} variant="outlined" />}
                  />
                </InfoLine>
              </Grid>
            </Grid>
          </GlassSubCard>
        </Grid>
      </Grid>
    </Box>
  );
};

CustomerProInfos.propTypes = {
  profession: PropTypes.string,
  specialite: PropTypes.string,
  modeOfExercise: PropTypes.string,
  adeliRpps: PropTypes.string,
  editing: PropTypes.bool,
  mdpMondpc: PropTypes.string,
  mdpFifpl: PropTypes.string,
  remplacant: PropTypes.bool,
  notCall: PropTypes.string,
  notRelaunch: PropTypes.bool,
  contactMethod: PropTypes.string,
  contactTime: PropTypes.string,
  linkedinProfileLink: PropTypes.string,
  siret: PropTypes.string,
  etablissement: PropTypes.string,
  startRelaunchDate: PropTypes.string,
  values: PropTypes.object,
  setFieldValue: PropTypes.func,
  handleChange: PropTypes.func,
};

export default CustomerProInfos;
